import Phaser from 'phaser';

import type { Point } from '../objects/StrokeEvaluator';

export const DEFAULT_TRACE_COLOR = '#007590';
const TRACE_OPACITY = { minDistance: 50, maxDistance: 100, minAlpha: 0.7 };

type TraceRendererConfig = {
	scene: Phaser.Scene;
	parent: Phaser.GameObjects.Container;
	width: number;
	color: string;
};

type CanvasGraphics = Phaser.GameObjects.Graphics & {
	renderCanvas: (
		renderer: Phaser.Renderer.Canvas.CanvasRenderer,
		src: Phaser.GameObjects.Graphics,
		camera: Phaser.Cameras.Scene2D.Camera,
		parentMatrix?: Phaser.GameObjects.Components.TransformMatrix,
		renderTargetCtx?: CanvasRenderingContext2D,
		allowClip?: boolean,
	) => void;
};

export class TraceRenderer {
	private readonly color: number;
	private graphics!: Phaser.GameObjects.Graphics;
	private points: Point[] = [];

	constructor(private readonly config: TraceRendererConfig) {
		this.color = Phaser.Display.Color.HexStringToColor(config.color).color;
	}

	beginStroke() {
		this.points = [];
		this.graphics = this.config.scene.add.graphics();
		this.config.parent.add(this.graphics);
		const graphics = this.graphics as CanvasGraphics;
		const renderCanvas = graphics.renderCanvas;
		// Phaser does not expose Canvas cap and join styles through Graphics commands.
		graphics.renderCanvas = (...args) => {
			const context = args[4] ?? args[0].currentContext;
			context.save();
			try {
				context.lineCap = 'round';
				context.lineJoin = 'round';
				renderCanvas.apply(graphics, args);
			} finally {
				context.restore();
			}
		};
	}

	setDistance(distance: number) {
		const { minDistance, maxDistance, minAlpha } = TRACE_OPACITY;
		const progress = Phaser.Math.Clamp((distance - minDistance) / (maxDistance - minDistance), 0, 1);
		this.graphics.setAlpha(Phaser.Math.Linear(1, minAlpha, progress));
	}

	drawPoint(point: Point) {
		if (this.points.length > 0) {
			return;
		}

		this.points.push({ ...point });
		this.graphics.fillStyle(this.color).fillCircle(point.x, point.y, this.config.width / 2);
	}

	drawSegment(from: Point, to: Point) {
		if (this.points.length === 0) {
			this.points.push({ ...from });
		}
		this.points.push({ ...to });
		this.graphics.clear().lineStyle(this.config.width, this.color).strokePoints(this.points);
	}

	clear() {
		this.points = [];
		this.config.parent.removeAll(true);
	}
}
